import { Injectable, Pipe, PipeTransform, inject } from '@angular/core';
import { ItemListGroup } from './item-list.component';
import { ProductGroupingStrategy } from '@pedix-workspace/utils';
import { ItemListGroupProductsByGroupPipe } from './item-list-group-products-by-group.pipe';
import { ItemListGroupProductsByCategoryPipe } from './item-list-group-products-by-category.pipe';
import { SortProductsByStockPipe } from '@pedix-workspace/pedixapp-shared-pipes';

export type ItemListProductGroupingStrategyParams<C> = {
  groupingStrategy: ProductGroupingStrategy;
  categories?: C[];
  undefinedGroupName?: string;
};

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'pxwItemListProductGroupingStrategy',
  standalone: true,
})
export class ItemListProductGroupingStrategyPipe<
  P extends { categoryId: string; groupName: string },
  C extends { id: string; name: string },
> implements PipeTransform
{
  private sortProductsByStockPipe = inject(SortProductsByStockPipe);
  private groupingStrategyByCategory = inject(ItemListGroupProductsByCategoryPipe);
  private groupingStrategyByProductGroup = inject(ItemListGroupProductsByGroupPipe);

  transform(
    products: P[],
    { groupingStrategy, categories, undefinedGroupName }: ItemListProductGroupingStrategyParams<C>,
  ): ItemListGroup<{ id: string; name: string }, P>[] {
    if (!Array.isArray(products) || products.length === 0) {
      return [];
    }
    if (groupingStrategy === 'category' && categories) {
      return this.groupingStrategyByCategory.transform(products, categories);
    }
    if (
      groupingStrategy === 'product-group' &&
      undefinedGroupName &&
      products.some(product => product.groupName)
    ) {
      return this.groupingStrategyByProductGroup.transform(products, undefinedGroupName);
    }
    const uniqueProducts = new Set<string>();

    return [
      {
        group: {
          id: 'none',
          name: '',
        },
        indexedItems: this.sortProductsByStockPipe
          .transform(products)
          .filter(product => {
            if (uniqueProducts.has(product.id)) {
              return false;
            }
            uniqueProducts.add(product.id);
            return true;
          })
          .map((product, listIndex) => ({
            listIndex,
            itemId: product.id,
            item: product,
          })),
      },
    ];
  }
}
